import { useState, useEffect, useMemo, useRef } from 'react'
import { X, Upload, Trash2, AlertCircle, ImageOff, Plus } from 'lucide-react'

// Editable team / league image list for one league. Every team in the current
// season is listed (matched to sched_images by the schedule's team name), then
// any image rows that aren't in this season, then league assets. Edits are
// staged and written in one PUT. CSV import (name,url — extra leading columns
// ignored) auto-matches names to schedule teams; anything it can't match is
// kept under its own name so it can be fixed by hand.
//
// props: league, teams[] (sched_teams for the season), images[], api, onChange(images), onClose

// "Mt. Lebanon" ≈ "Mt Lebanon", "Canon Mac" ≈ "Canon-McMillan", "Beaver Area Hockey Club" ≈ "Beaver"
const ALIASES = { 'canon mac': 'canon mcmillan' }
export function normName(s) {
  let n = String(s || '').toLowerCase().trim().replace(/\./g, '').replace(/-/g, ' ').replace(/&/g, 'and')
  n = n.replace(/\b(area hockey club|falcons hockey|hockey club|varsity)\b/g, '')
  n = n.replace(/\bst\b/g, 'saint').replace(/\s+/g, ' ').trim()
  return ALIASES[n] || n
}

function parseCsv(text) {
  // minimal CSV: handles quoted fields with commas
  return text.split(/\r?\n/).map(line => {
    const out = []; let cur = ''; let q = false
    for (let i = 0; i < line.length; i++) {
      const c = line[i]
      if (c === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++ } else q = !q }
      else if (c === ',' && !q) { out.push(cur); cur = '' }
      else cur += c
    }
    out.push(cur)
    return out.map(x => x.trim())
  })
}

// Defined at module level so React keeps the input mounted (and focused) across parent re-renders.
function ImageRow({ r, sub, onUrl, onColor, onRemove }) {
  const [broken, setBroken] = useState(false)
  useEffect(() => { setBroken(false) }, [r.image_url])
  return (
    <div className={`ts-img-row ${r.dirty ? 'is-dirty' : ''}`}>
      <div className="ts-img-thumb" title={broken ? 'Image failed to load' : ''}>
        {r.image_url && !broken ? <img src={r.image_url} alt="" loading="lazy" onError={() => setBroken(true)} /> : broken ? <AlertCircle size={16} className="text-danger" /> : <ImageOff size={16} />}
      </div>
      <div className="ts-img-name">
        <strong>{r.name}</strong>
        {sub && <span className="muted">{sub}</span>}
        {r.source_name && r.source_name !== r.name && <span className="muted" title="Name as imported">imported as “{r.source_name}”</span>}
      </div>
      <input
        type="url" inputMode="url" value={r.image_url} onChange={e => onUrl(r.name, e.target.value)}
        placeholder="https://imagedelivery.net/…/public" aria-label={`Image URL for ${r.name}`}
      />
      {r.kind !== 'asset' ? (
        <span className="ts-img-colorwrap" title="Team color — used for the team-name text on generated thumbnails">
          <input type="color" value={r.primary_color || '#1a1a2e'} onChange={e => onColor(r.name, e.target.value)} aria-label={`Team color for ${r.name}`} />
          {r.primary_color ? <button type="button" className="ts-linkbtn" onClick={() => onColor(r.name, '')} title="Clear color">×</button> : <span className="muted">none</span>}
        </span>
      ) : <span />}
      <button type="button" className="ts-iconbtn" onClick={() => onRemove(r)} title="Remove entry" aria-label={`Remove ${r.name}`}><Trash2 size={14} /></button>
    </div>
  )
}

export default function TeamImages({ league, teams, images, api, onChange, onClose }) {
  const [rows, setRows] = useState({})       // name → { name, kind, image_url, source_name, id?, dirty? }
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [importNote, setImportNote] = useState('')
  const [newName, setNewName] = useState('')
  const fileRef = useRef(null)

  // Seed the editor: season teams first (with whatever image they have), then the rest.
  useEffect(() => {
    const byName = new Map(images.map(i => [i.name, i]))
    const next = {}
    teams.forEach(t => { const i = byName.get(t.name); next[t.name] = { name: t.name, kind: 'team', image_url: i?.image_url || '', source_name: i?.source_name || null, primary_color: i?.primary_color || null, id: i?.id } })
    images.forEach(i => { if (!next[i.name]) next[i.name] = { ...i, image_url: i.image_url || '' } })
    setRows(next)
  }, [teams, images])

  const teamNames = useMemo(() => new Set(teams.map(t => t.name)), [teams])
  const divisionOf = useMemo(() => new Map(teams.map(t => [t.name, t.division])), [teams])
  const list = Object.values(rows)
  const inSeason = list.filter(r => teamNames.has(r.name)).sort((a, b) => (divisionOf.get(a.name) || '').localeCompare(divisionOf.get(b.name) || '') || a.name.localeCompare(b.name))
  const others = list.filter(r => !teamNames.has(r.name) && r.kind !== 'asset').sort((a, b) => a.name.localeCompare(b.name))
  const assets = list.filter(r => r.kind === 'asset').sort((a, b) => a.name.localeCompare(b.name))
  const dirty = list.filter(r => r.dirty)
  const missing = inSeason.filter(r => !r.image_url).length

  const setUrl = (name, image_url) => setRows(r => ({ ...r, [name]: { ...r[name], image_url, dirty: true } }))
  const setColor = (name, primary_color) => setRows(r => ({ ...r, [name]: { ...r[name], primary_color: primary_color || null, dirty: true } }))

  function addRow(kind) {
    const name = newName.trim()
    if (!name || rows[name]) return
    setRows(r => ({ ...r, [name]: { name, kind, image_url: '', dirty: true } }))
    setNewName('')
  }

  async function removeRow(row) {
    if (!window.confirm(`Remove the image entry for "${row.name}"?`)) return
    if (row.id) {
      try { await api.deleteImage(row.id) } catch (e) { setErr(e.body?.error || e.message); return }
    }
    setRows(r => { const n = { ...r }; delete n[row.name]; return n })
    if (row.id) onChange(images.filter(i => i.id !== row.id))
  }

  function importCsv(file) {
    const reader = new FileReader()
    reader.onload = () => {
      const lines = parseCsv(String(reader.result || ''))
      const bySched = new Map(teams.map(t => [normName(t.name), t.name]))
      let matched = 0, unmatched = 0, assetsN = 0
      setRows(prev => {
        const next = { ...prev }
        for (const cells of lines) {
          // name = last non-URL cell before the url; url = last cell that looks like a URL
          const url = [...cells].reverse().find(c => /^https?:\/\//i.test(c)) || ''
          const name = cells.filter(c => c && !/^https?:\/\//i.test(c)).pop() || ''
          if (!name || name.toLowerCase() === league.toLowerCase()) continue
          if (/^\w+\d$/.test(name) && url && Object.values(next).some(r => r.image_url === url)) continue   // "Avonworth2" dup
          const isAsset = new RegExp(`\\b${league}\\b|logo|channel|playoff|charit`, 'i').test(name) && !bySched.has(normName(name))
          const target = isAsset ? name : (bySched.get(normName(name)) || name)
          if (isAsset) assetsN++; else if (bySched.has(normName(name))) matched++; else unmatched++
          next[target] = { ...(next[target] || { name: target, kind: isAsset ? 'asset' : 'team' }), image_url: url || next[target]?.image_url || '', source_name: target !== name ? name : (next[target]?.source_name || null), dirty: true }
        }
        return next
      })
      setImportNote(`Imported: ${matched} matched to schedule teams, ${unmatched} unmatched (kept by name), ${assetsN} league assets. Review, then Save.`)
    }
    reader.readAsText(file)
  }

  async function save() {
    if (!dirty.length) return
    setBusy(true); setErr('')
    try {
      const { images: list } = await api.upsertImages(league, dirty.map(r => ({ name: r.name, kind: r.kind, image_url: r.image_url, source_name: r.source_name, primary_color: r.primary_color })))
      onChange(list)
      setImportNote('')
    } catch (e) {
      setErr(e.body?.error || e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ts-modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose() }}>
      <div className="ts-modal ts-modal-wide" role="dialog" aria-modal="true" aria-labelledby="ts-img-title">
        <div className="ts-modal-head">
          <h2 id="ts-img-title">Team images <span className="muted">· {league} · {inSeason.length - missing}/{inSeason.length} teams have a logo</span></h2>
          <button type="button" className="ts-iconbtn" onClick={() => fileRef.current?.click()} title="Import CSV (name, url)" aria-label="Import CSV"><Upload size={16} /></button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={e => { if (e.target.files?.[0]) importCsv(e.target.files[0]); e.target.value = '' }} />
          <button type="button" className="ts-iconbtn" onClick={onClose} aria-label="Close" disabled={busy}><X size={16} /></button>
        </div>

        <div className="ts-img-body">
          {importNote && <p className="ts-img-note">{importNote}</p>}

          <h3 className="ts-img-h">This season</h3>
          {inSeason.map(r => <ImageRow key={r.name} r={r} sub={divisionOf.get(r.name)} onUrl={setUrl} onColor={setColor} onRemove={removeRow} />)}

          {others.length > 0 && (
            <>
              <h3 className="ts-img-h">Other teams <span className="muted">· not in this season</span></h3>
              {others.map(r => <ImageRow key={r.name} r={r} onUrl={setUrl} onColor={setColor} onRemove={removeRow} />)}
            </>
          )}

          {assets.length > 0 && (
            <>
              <h3 className="ts-img-h">League assets</h3>
              {assets.map(r => <ImageRow key={r.name} r={r} onUrl={setUrl} onColor={setColor} onRemove={removeRow} />)}
            </>
          )}

          <div className="ts-img-add">
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Add a team or asset by name" aria-label="New entry name" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRow('team') } }} />
            <button type="button" onClick={() => addRow('team')} disabled={!newName.trim()}><Plus size={13} /> Team</button>
            <button type="button" onClick={() => addRow('asset')} disabled={!newName.trim()}><Plus size={13} /> Asset</button>
          </div>

          {err && <p className="ts-tpl-warn"><AlertCircle size={13} /> {err}</p>}

          <div className="ts-modal-actions">
            <span className="muted">{dirty.length ? `${dirty.length} unsaved change${dirty.length === 1 ? '' : 's'}` : 'No unsaved changes'}</span>
            <span className="ts-spacer" />
            <button type="button" onClick={onClose} disabled={busy}>Close</button>
            <button type="button" onClick={save} disabled={busy || !dirty.length}>{busy ? 'Saving…' : 'Save changes'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}
